import * as yup from 'yup';
import { useForm, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import DOMPurify from 'dompurify';
import { useGenerateLinkedInMessageOutreach, useLinkedInMessageOutreach } from '../../../../api/useOutreachCandidate';
import YupValidationErrors from '@/Utils/YupValidationErrors';

export interface LinkedInMessageTemplateForm {
    subject: string;
    messageHtml: string;
    htmlTextLength: number;
}

export const LinkedInMessageLimit = 1900;

const linkedInMessageSchema: yup.SchemaOf<LinkedInMessageTemplateForm> = yup.object({
    messageHtml: yup.string().required(),
    htmlTextLength: yup
        .number()
        .required()
        .moreThan(0, YupValidationErrors.Required)
        .max(LinkedInMessageLimit, YupValidationErrors.PleaseLimitDescriptionError),
    subject: yup
        .string()
        .required(YupValidationErrors.Required)
        .max(200, YupValidationErrors.MaxSymbolsValidationError),
});

export const useLinkedInMessageForm = () => {
    // this should be the only place to handle ai generated outreach
    // unless if you are using useSWR/useSWRImmut
    const { trigger } = useGenerateLinkedInMessageOutreach();
    const { data: savedTemplate } = useLinkedInMessageOutreach();

    return useForm({
        resolver: yupResolver(linkedInMessageSchema),
        mode: 'all',
        defaultValues: async () => {
            const defaultValues: LinkedInMessageTemplateForm = {
                messageHtml: '',
                htmlTextLength: 0,
                subject: '',
            };

            if (savedTemplate) {
                const parser = new DOMParser();
                const dom = parser.parseFromString(savedTemplate.message, 'text/html');

                defaultValues.messageHtml = DOMPurify.sanitize(savedTemplate.message);
                defaultValues.htmlTextLength = dom.body?.textContent?.length || 0;
                defaultValues.subject = savedTemplate.subject;
            } else {
                try {
                    const generatedOutreach = await trigger();
                    const messageHtml = generatedMessageToHtml(generatedOutreach.message);

                    defaultValues.messageHtml = DOMPurify.sanitize(messageHtml);
                    defaultValues.htmlTextLength = generatedOutreach.message.length;
                    defaultValues.subject = generatedOutreach.subject;
                } catch {
                    return defaultValues;
                }
            }

            return defaultValues;
        },
    });
};

type LinkedInMessage = yup.InferType<typeof linkedInMessageSchema>;
export const useLinkedInMessageFormContext = () => useFormContext<LinkedInMessage>();

// only use for outreach strings generated by AI
const generatedMessageToHtml = (messageBody: string) => {
    const nodes = messageBody.split(/\n/g);
    const encodedMessageBody = nodes
        // fully encode to html paragraphs
        .map((node) => `<p>${node}</p>`)
        // transform your nodes here
        .map((node, i, arr) => {
            const listSearchValue = '<p>- ';

            // transform node starting with dash to unordered list
            if (node.startsWith(listSearchValue)) {
                const newNode = `<li>${node.replace(listSearchValue, '<p>')}</li>`;

                if (!arr[i - 1].startsWith(listSearchValue)) {
                    return `<ul>${newNode}`;
                } else if (!arr[i + 1].startsWith(listSearchValue)) {
                    return `${newNode}</ul>`;
                } else {
                    return newNode;
                }
            } else {
                return node;
            }
        })
        .join('');

    return encodedMessageBody;
};
